import React, { useState } from "react";
import { X, ArrowRight, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";

const EJS_SERVICE  = "service_amcggz8";
const EJS_TEMPLATE = "template_1pbqpeh";
const EJS_PUBLIC   = "xD0TgYHKdR8Y75yUE";

export default function DemoDialog({ open, onClose }) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    size: "11-50",
    channels: [],
  });

  if (!open) return null;

  const handleChannel = (c) => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(c)
        ? f.channels.filter((x) => x !== c)
        : [...f.channels, c],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in your name and work email.");
      return;
    }

    setLoading(true);
    try {
      await emailjs.send(
        EJS_SERVICE,
        EJS_TEMPLATE,
        {
          name:      form.name,
          email:     form.email,
          company:   form.company,
          team_size: form.size,
          channels:  form.channels.length ? form.channels.join(", ") : "None selected",
        },
        EJS_PUBLIC
      );
      setSubmitted(true);
      toast.success("Demo request received — we'll be in touch within 24 hours.");
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setForm({ name: "", email: "", company: "", size: "11-50", channels: [] });
      }, 1800);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="demo-dialog"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="demo-dialog-backdrop"
      />
      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d0d14] overflow-hidden shadow-[0_40px_100px_-20px_rgba(0,0,0,0.7)] fade-up">
        <button
          onClick={onClose}
          data-testid="demo-dialog-close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-white/70" />
        </button>

        {submitted ? (
          <div className="p-10 text-center" data-testid="demo-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#3DDFB3]/15 border border-[#3DDFB3]/40 flex items-center justify-center mb-5">
              <Check className="w-6 h-6 text-[#3DDFB3]" />
            </div>
            <h3 className="font-serif text-3xl text-white mb-2">
              You're on the list.
            </h3>
            <p className="text-white/55 text-[14.5px]">
              Our team will reach out within 24 hours with a tailored walkthrough.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 md:p-10" data-testid="demo-form">
            <div className="mb-6">
              <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-[#7C5CFF]">
                ── Request demo
              </span>
              <h3 className="font-serif text-3xl md:text-4xl text-white mt-3 leading-tight">
                See your agents{" "}
                <span className="italic">live</span> in 30 minutes.
              </h3>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label="Full name"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                  placeholder="Ada Lovelace"
                  testid="demo-input-name"
                />
                <Field
                  label="Work email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  placeholder="ada@company.com"
                  type="email"
                  testid="demo-input-email"
                />
              </div>
              <Field
                label="Company"
                value={form.company}
                onChange={(v) => setForm({ ...form, company: v })}
                placeholder="Acme Co."
                testid="demo-input-company"
              />

              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-white/50 mb-2">
                  Team size
                </label>
                <div className="flex flex-wrap gap-2">
                  {["1-10", "11-50", "51-200", "201-1000", "1000+"].map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => setForm({ ...form, size: s })}
                      data-testid={`demo-size-${s}`}
                      className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                        form.size === s
                          ? "bg-[#7C5CFF] border-[#7C5CFF] text-white"
                          : "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/20"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-white/50 mb-2">
                  Channels you care about
                </label>
                <div className="flex flex-wrap gap-2">
                  {["WhatsApp", "Email", "Web chat", "Voice", "Slack", "IG"].map(
                    (c) => (
                      <button
                        type="button"
                        key={c}
                        onClick={() => handleChannel(c)}
                        data-testid={`demo-channel-${c.toLowerCase().replace(" ", "-")}`}
                        className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${
                          form.channels.includes(c)
                            ? "bg-[#3DDFB3]/15 border-[#3DDFB3]/50 text-[#3DDFB3]"
                            : "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/20"
                        }`}
                      >
                        {c}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="demo-submit-btn"
              className="mt-8 btn-primary w-full py-3 rounded-full text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  Book my slot
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="mt-4 text-[11px] text-white/40 text-center">
              By submitting you agree to our Privacy Policy. No spam, ever.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-[0.12em] text-white/50 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 text-[14px] text-white placeholder:text-white/30 focus:outline-none focus:border-[#7C5CFF] focus:bg-white/[0.05] transition-colors"
      />
    </div>
  );
}
